<template>
  <div>
    <ConfirmDialog
      :dialog="showConfirmDelete"
      @modalResult="confirmDeleteAlignerDialogResult"
      title="Delete Equipment"
      :text=getDeleteText()
      yesColor="#f5f5f5"
    >
    </ConfirmDialog>

    <AlignerDetailDialog
      :dialog="showAlignerDetailDialog"
      :aligner="aligner"
      @close="closeAlignerDetailDialog"
      @refresh="refreshAlignerDetailDialog"
    >
    </AlignerDetailDialog>

    <SupportTicketDialog
      :dialog="showSupportTicketDialog"
      :aligner="aligner"
      @close="closeSupportTicketDialog"
    >
    </SupportTicketDialog>

    <AlignerScreenShotDialog
      :dialog="showAlignerScreenShotDialog"
      :aligner="aligner"
      @close="closeAlignerScreenShotDialog"
      @refresh="refreshAlignerScreenShots"
    >
    </AlignerScreenShotDialog>

    <AlignerVoltagesDialog
      :dialog="showAlignerVoltagesDialog"
      :aligner="aligner"
      @close="closeAlignerVoltagesDialog"
    >
    </AlignerVoltagesDialog>

    <AlignerPrinterStatusDialog
      :dialog="showAlignerPrinterStatusDialog"
      :aligner="aligner"
      @close="closeAlignerPrinterStatusDialog"
    >
    </AlignerPrinterStatusDialog>

    <AlignerUsbStatusDialog
      :dialog="showAlignerUsbStatusDialog"
      :aligner="aligner"
      @close="closeAlignerUsbStatusDialog"
    >
    </AlignerUsbStatusDialog>

    <TicketHistoryDialog
      :dialog="showTicketHistoryDialog"
      :serialNumber="serialNumber"
      :tickets="tickets"
      @close="closeTicketHistoryDialog"
    >
    </TicketHistoryDialog>

    <v-card>
      <v-toolbar
        color="light-blue"
      >
        <v-toolbar-title>
          Equipment
        </v-toolbar-title>
        <div class="flex-grow-1"></div>
        <div style="width: 200px;" class="pr-5">
          <v-text-field
            placeholder="filter country code"
            single-line
            v-model="filterCountryCode"
            append-icon="filter_list"
            class="mt-4 mr-3"
            @input="filter"
          >
          </v-text-field>
        </div>
        <div style="width: 250px;" class="pr-5">
          <v-text-field
            placeholder="filter software version"
            single-line
            v-model="filterSoftwareVersion"
            append-icon="filter_list"
            class="mt-4 mr-3"
            @input="filter"
          >
          </v-text-field>
        </div>
        <div style="width: 300px;">
          <v-text-field
            placeholder="filter family"
            single-line
            v-model="filterFamily"
            append-icon="filter_list"
            class="mt-4 mr-3"
            @input="filter"
          >
          </v-text-field>
        </div>
        <div style="width: 300px;">
          <v-text-field
            placeholder="filter serial number"
            single-line
            v-model="filterSerialNumber"
            append-icon="filter_list"
            class="mt-4 mr-3"
            @input="filter"
          >
          </v-text-field>
        </div>
      </v-toolbar>

      <v-data-table
        class="mt-5"
        single-select
        :footer-props="{ disableItemsPerPage: true, showFirstLastPage: true, showCurrentPage: true  }"
        :headers="headers"
        :loading="busy"
        :items="aligners"
        :items-per-page="10"
        :server-items-length="serverItemsLength"
        :options.sync="options"
      >
        <template v-slot:item.softwareVersion="{ item }">
          <v-chip small :color="getVersionColor(item.softwareVersion, item.family)" dark>{{ item.softwareVersion }}</v-chip>
        </template>

        <!--
        <template v-slot:item.specsExpiration="{ item }">
          <div>{{ getSpecsExpiration(item) }}</div>
        </template>
        -->

        <template v-slot:item.flag="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <img class="mt-2" :src="getFlag(item)" v-on="on">
            </template>
            <span>{{item.geolocation.country_code}}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.action="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click.stop="loadAndShowAlignerDetailDialog(item)" v-on="on">zoom_in</v-icon>
            </template>
            <span>View Aligner Details</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click.stop="loadAndShowSupportTicketDialog(item)" v-on="on">bug_report</v-icon>
            </template>
            <span>Create Support Ticket</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click.stop="loadAndShowTicketHistory(item)" v-on="on">list_alt</v-icon>
            </template>
            <span>View Ticket History</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-if="canShowScreenShots(item)" small class="mr-2" @click.stop="loadAndShowScreenShotDialog(item)" v-on="on">camera_alt</v-icon>
            </template>
            <span>View Screen Shots</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-if="canShowDelete(item)" small class="mr-2" @click.stop="openConfirmDeleteDialog(item)" v-on="on" style="color:red;">delete_forever</v-icon>
            </template>
            <span>Delete Equipment</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-if="canShowVoltageReport(item)" small class="mr-2" @click.stop="loadAndShowVoltagesDialog(item)" v-on="on">electric_car</v-icon>
            </template>
            <span>View Last Voltage Report</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-if="canShowUsbStatus(item)" small class="mr-2" @click.stop="loadAndShowUsbStatusDialog(item)" v-on="on">usb</v-icon>
            </template>
            <span>View Last USB Status Report</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-if="canShowPrinterStatus(item)" small class="mr-2" @click.stop="loadAndShowPrinterStatusDialog(item)" v-on="on">print</v-icon>
            </template>
            <span>View Last Printer Status Report</span>
          </v-tooltip>

        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import moment from 'moment';
import semver from 'semver';
import ConfirmDialog from '../components/ConfirmDialog.vue';
import AlignerDetailDialog from '../components/aligners/AlignerDetailDialog.vue';
import SupportTicketDialog from '../components/support_tickets/SupportTicketDialog.vue';
import AlignerScreenShotDialog from '../components/aligners/AlignerScreenShotDialog.vue';
import TicketHistoryDialog from '../components/support_tickets/TicketHistoryDialog.vue';
import AlignerVoltagesDialog from '../components/aligners/AlignerVoltagesDialog.vue';
import AlignerUsbStatusDialog from '../components/aligners/AlignerUsbStatusDialog.vue';
import AlignerPrinterStatusDialog from '../components/aligners/AlignerPrinterStatusDialog.vue';

export default {
  components: {
    AlignerDetailDialog,
    SupportTicketDialog,
    AlignerScreenShotDialog,
    TicketHistoryDialog,
    AlignerVoltagesDialog,
    AlignerUsbStatusDialog,
    AlignerPrinterStatusDialog,
    ConfirmDialog
  },

  data() {
    return {
      headers: [
        {
          text: 'Serial Number',
          align: 'left',
          sortable: true,
          value: 'serialNumber',
          width: 175,
        },
        {
          text: 'Country',
          align: 'left',
          sortable: true,
          value: 'flag',
          width: 125,
        },
        {
          text: 'Family',
          align: 'left',
          sortable: true,
          value: 'family',
          width: 125,
        },
        {
          text: 'Software Version',
          align: 'left',
          sortable: true,
          value: 'softwareVersion',
          width: 275,
        },
        /*
        {
          text: 'Specs Version',
          align: 'left',
          sortable: false,
          value: 'specs.name',
          width: 175,
        },
        {
          text: 'Specs Expiration',
          align: 'left',
          sortable: false,
          value: 'specsExpiration',
          width: 175,
        },
        */
        {
          text: 'Created At',
          align: 'left',
          sortable: true,
          value: 'createdAt',
          width: 200,
        },
        {
          text: 'Updated At',
          align: 'left',
          sortable: true,
          value: 'updatedAt',
          width: 200,
        },
        {
          text: 'Actions',
          align: 'left',
          sortable: false,
          value: 'action',
        },
      ],
      options: {},
      busy: false,
      serverItemsLength: 0,
      aligners: [],
      filterCountryCode: '',
      filterSerialNumber: '',
      filterSoftwareVersion: '',
      filterFamily: '',
      aligner: {},
      serialNumber: '',
      tickets: [],
      showAlignerDetailDialog: false,
      showSupportTicketDialog: false,
      showAlignerScreenShotDialog: false,
      showTicketHistoryDialog: false,
      showAlignerVoltagesDialog: false,
      showAlignerPrinterStatusDialog: false,
      showAlignerUsbStatusDialog: false,
      showConfirmDelete: false,
    };
  },

  watch: {
    options: {
      handler() {
        this.getData();
      },
      deep: true,
    },
  },

  mounted() {
    this.getData();
  },

  methods: {
    getData() {
      //this.$store.dispatch('getLatestSoftwareVersion');

      //console.log(JSON.stringify(this.options, null, 2));

      const options = {
        method: 'get',
        url: `/read/aligners/v1/?page=${this.options.page || 1}&filterSerialNumber=${this.filterSerialNumber}&filterCountryCode=${this.filterCountryCode}&filterSoftwareVersion=${this.filterSoftwareVersion}&filterFamily=${this.filterFamily}&sortBy=${this.options.sortBy || ''}&sortDesc=${this.options.sortDesc || false}`,
      };

      this.busy = true;

      this.axios(options)
        .then((res) => {
          this.busy = false;
          this.serverItemsLength = res.data.totalDocs;
          this.aligners = res.data.docs.map((cur) => {
            const {
              createdAt, updatedAt, version, ...rest
            } = cur;
            const created = moment(createdAt);
            const updated = moment(updatedAt);
            const softwareVersion = `${version.semantic}${version.meta ? ` - ${version.meta}` : ''}`;
            return Object.assign({}, {
              createdAt: created.local().format('YYYY-MM-DD HH:mm:ss'),
              updatedAt: updated.local().format('YYYY-MM-DD HH:mm:ss'),
              softwareVersion,
            }, rest);
          });
        })
        .catch((err) => {
          this.busy = false;
        });
    },

    filter() {
      this.getData();
    },

    loadAndShowAlignerDetailDialog(aligner) {
      const options = {
        method: 'get',
        url: `/read/aligner/v1/?serialNumber=${aligner.serialNumber}&username=${this.$store.state.auth.token.user.username}`,
      };

      this.busy = true;

      this.axios(options)
        .then((res) => {
          this.busy = false;

          // console.log(JSON.stringify(res.data, null, 2));

          // if (res.data.updateState) {
          //  console.log('----------- aligner update state:');
          //  console.log(JSON.stringify(res.data.updateState, null, 2));
          // }

          this.aligner = res.data;
          this.showAlignerDetailDialog = true;
        });
    },

    refreshAlignerDetailDialog() {
      const options = {
        method: 'get',
        url: `/read/aligner/v1/?serialNumber=${this.aligner.serialNumber.toUpperCase()}&username=${this.$store.state.auth.token.user.username}`,
      };

      this.axios(options)
        .then((res) => {
          this.aligner = res.data;
        });
    },

    closeAlignerDetailDialog() {
      this.showAlignerDetailDialog = false;
      this.aligner = {};
    },

    loadAndShowSupportTicketDialog(aligner) {
      const options = {
        method: 'get',
        url: `/read/aligner/v1/?serialNumber=${aligner.serialNumber.toUpperCase()}&username=${this.$store.state.auth.token.user.username}`,
      };

      this.busy = true;

      this.axios(options)
        .then((res) => {
          this.busy = false;

          this.aligner = res.data;
          this.showSupportTicketDialog = true;
        });
    },

    closeSupportTicketDialog() {
      this.showSupportTicketDialog = false;
      this.aligner = {};
    },

    loadAndShowScreenShotDialog(aligner) {
      const options = {
        method: 'get',
        url: `/api/v1/get_screen_shots_for_aligner?serialNumber=${aligner.serialNumber.toUpperCase()}`,
      };

      this.busy = true;

      this.axios(options)
        .then((res) => {
          this.busy = false;

          this.aligner = res.data;
          this.showAlignerScreenShotDialog = true;
        });
    },

    refreshAlignerScreenShots(aligner) {
      const options = {
        method: 'get',
        url: `/api/v1/get_screen_shots_for_aligner?serialNumber=${aligner.serialNumber.toUpperCase()}`,
      };

      this.axios(options)
        .then((res) => {
          this.aligner = res.data;
        });
    },

    closeAlignerScreenShotDialog() {
      this.showAlignerScreenShotDialog = false;
      this.aligner = {};
    },

    loadAndShowTicketHistory(aligner) {
      const options = {
        method: 'get',
        url: `/read/ticketHistory/v1?serialNumber=${aligner.serialNumber.toUpperCase()}`,
      };

      this.busy = true;
      this.serialNumber = aligner.serialNumber;

      this.axios(options)
        .then((res) => {
          this.busy = false;

          this.tickets = res.data;
          this.showTicketHistoryDialog = true;
        });
    },

    closeTicketHistoryDialog() {
      this.serialNumber = '';
      this.showTicketHistoryDialog = false;
      this.tickets = [];
    },

    loadAndShowVoltagesDialog(aligner) {
      const options = {
        method: 'get',
        url: `/api/v1/get_latest_voltages_for_aligner?serialNumber=${aligner.serialNumber.toUpperCase()}`,
      };

      this.busy = true;

      this.axios(options)
        .then((res) => {
          this.busy = false;

          this.aligner = res.data;
          this.showAlignerVoltagesDialog = true;

          console.log('show voltages dialog now!!');
        });
    },

    closeAlignerVoltagesDialog() {
      this.showAlignerVoltagesDialog = false;
      this.aligner = {};
    },

    loadAndShowPrinterStatusDialog(aligner) {
      console.log('loadAndShowPrinterStatusDialog');

      const options = {
        method: 'get',
        url: `/api/v1/get_latest_printer_status_for_aligner?serialNumber=${aligner.serialNumber.toUpperCase()}`,
      };

      this.busy = true;

      this.axios(options)
        .then((res) => {
          this.busy = false;

          console.log('--------------------------------');
          console.log(JSON.stringify(res.data, null, 2));

          this.aligner = res.data;
          this.showAlignerPrinterStatusDialog = true;

          console.log('show that printer status dialog now!!');
        });
    },

    closeAlignerPrinterStatusDialog() {
      this.showAlignerPrinterStatusDialog = false;
      this.aligner = {};
    },

    loadAndShowUsbStatusDialog(aligner) {
      const options = {
        method: 'get',
        url: `/api/v1/get_latest_usb_status_for_aligner?serialNumber=${aligner.serialNumber.toUpperCase()}`,
      };

      this.busy = true;

      this.axios(options)
        .then((res) => {
          this.busy = false;

          this.aligner = res.data;
          this.showAlignerUsbStatusDialog = true;
        });
    },

    closeAlignerUsbStatusDialog() {
      this.showAlignerUsbStatusDialog = false;
      this.aligner = {};
    },

    getVersionColor(version, family) {
      let color = 'green';

      if (version) {
        const v = version.split(/_| /)[0];

        if (family === 'aligner-oasis' && semver.gt(this.$store.state.latestOasisSoftwareVersion.semantic, v)) {
          color = 'red';
        } else if (family === 'gtps' && semver.gt(this.$store.state.latestGtpsSoftwareVersion.semantic, v)) {
          color = 'red';
        } else if (family === 'aligner' && semver.gt(this.$store.state.latestSoftwareVersion.semantic, v)) {
          color = 'red';
        }
      }

      return color;
    },

    getSpecsExpiration(aligner) {
      if (aligner.specsExpiration) {
        return `${aligner.specsExpiration.month}/${aligner.specsExpiration.year}`;
      }
      return 'unknown';
    },

    getFlag(aligner) {
      if (aligner.geolocation) {
        return `/flags/${aligner.geolocation.country_code.toLowerCase()}.png`;
      }

      return '';
    },

    canShowScreenShots(item) {
      let showIt = false;

      if (item && item.softwareVersion) {
        const v = item.softwareVersion.split(/_| /)[0];
        if (
          (item.family === 'aligner' && semver.gte(v, '4.3.0') && item.systemClass !== 'DRAGON_HEADLESS')
          || (item.family === 'gtps' && semver.gte(v, '1.0.0')
          || (item.family === 'aligner-oasis' && semver.gte(v, '1.0.0')))
        ) {
          showIt = true;
        }
      }

      return showIt;
    },

    canShowDelete() {
      return (
        this.$store.state.auth.token
        && (
          this.$store.state.auth.token.user.role === 'admin'
          || this.$store.state.auth.token.user.role === 'engineer'
        )
      );
    },

    canShowUsbStatus(item) {
      let showIt = false;

      /*
      if (item && item.softwareVersion) {
        const v = item.softwareVersion.split(' ')[0];
        if (item.family === 'aligner' && semver.gte(v, '5.0.0')) {
          showIt = true;
        }
      }
      */

      return showIt;
    },

    canShowPrinterStatus(item) {
      let showIt = false;

      /*
      if (item && item.softwareVersion) {
        const v = item.softwareVersion.split(' ')[0];
        if (item.family === 'aligner' && semver.gte(v, '5.0.0')) {
          showIt = true;
        }
      }
      */

      return showIt;
    },

    canShowVoltageReport(item) {
      let showIt = false;

      /*
      if (item && item.softwareVersion) {
        const v = item.softwareVersion.split(' ')[0];
        if (item.family === 'aligner' && semver.gte(v, '5.0.0')) {
          showIt = true;
        }
      }
      */

      return showIt;
    },

    getDeleteText() {
      return (`Are you sure you want to delete ${this.aligner.serialNumber}?`);
    },

    openConfirmDeleteDialog(aligner) {
      this.aligner = aligner;
      this.showConfirmDelete = true;
    },

    confirmDeleteAlignerDialogResult(confirmed) {
      if (confirmed) {
        const options = {
          method: 'post',
          url: '/api/v1/delete_equipment',
          data: {
            serialNumber: this.aligner.serialNumber,
          },
        };

        this.axios(options)
          .then((res) => {
            this.showConfirmDelete = false;
            this.aligner = {};

            this.getData();
          });
      } else {
        this.showConfirmDelete = false;
        this.aligner = {};
      }
    },

  },
};
</script>

<style scoped>
</style>
